import React, { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import Button from '@atlaskit/button';
import { Box } from '@atlaskit/primitives/compiled';
import { Section } from '@atlaskit/side-navigation';
import { useOgTenantInfo } from '@atlassian/jira-eoc-tenant-info/src/controllers/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { SpotlightTarget } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/spotlight-target/index.tsx';
import { TitleHeader } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/title-header/main.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { ConnectMenu } from '@atlassian/jira-navigation-apps-sidebar-connect/src/common/ui/connect-menu/main.tsx';
import { useProjectQueuesConnectItems } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project-queues/index.tsx';
import ForgeServiceDeskQueueSection from '@atlassian/jira-navigation-apps-sidebar-forge/src/ui/servicedesk-queue/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { PRACTICES_IDS } from '@atlassian/jira-servicedesk-common/src/constants.tsx';
import { QUEUE_ROUTES } from '@atlassian/jira-servicedesk-navigation/src/utils/queues/index.tsx';
import { useQueueCustomRouterContext } from '@atlassian/jira-servicedesk-navigation/src/utils/queues/use-queue-custom-router-context/index.tsx';
import { useSelectedGroupIdByCategory } from '@atlassian/jira-servicedesk-queues-categorized-store/src/controllers/navigation-category/index.tsx';
import { usePriorityGroups } from '@atlassian/jira-servicedesk-queues-categorized-store/src/controllers/priority-group/index.tsx';
import { ExperienceFailureTracker } from '@atlassian/jira-common-experience-tracking-viewing/src/view/experience-tracker-consumer/result-declared/index.tsx';
import {
	useCategorizedNavItems,
	usePracticeMetadata,
} from '@atlassian/jira-servicedesk-queues-categorized-store/src/main.tsx';
import {
	CHANGES,
	INCIDENTS,
	type ItsmPractice,
	NO_CATEGORY,
} from '@atlassian/jira-servicedesk-work-category/src/common/constants.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { useOnFavoritesChanged } from '../../../controllers/practice-queues/favoriting/index.tsx';
import { useQueueFeatures } from '../../utils/queue-features/index.tsx';
import AllQueuesSection from './all-queues-section/index.tsx';
import { QueuesExperienceTracker } from './experience-tracker/main.tsx';
import { GiveFeedbackItem } from './give-feedback-item/index.tsx';
import { GotoOpsgenieMenuItem } from './goto-opsgenie/index.tsx';
import { ManageQueuesItem } from './manage-queues-item/index.tsx';
import { messages } from './messages.tsx';
import { QueueGroupsSection } from './queue-groups/index.tsx';
import { StarredSection } from './starred-section/index.tsx';
import type { PracticeQueuesProps } from './types.tsx';

// @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
const updateConnectUrl = (item) => ({
	...item,
	url: item.url?.replace('/projects/', '/jira/servicedesk/projects/'),
	items: item.items?.map(updateConnectUrl),
});

/**
 * @deprecated This is a Nav3 component. If you do any changes here, please apply and QA them at Nav4 component as well: src/packages/navigation-apps/sidebar-nav4
 */
const SectionsWithQueueGroups = ({
	currentCategory,
	projectKey,
	onRefreshGroup,
	selectedGroupId,
	onGroupChange,
	canManageQueues,
	renderSpotlight,
}: {
	currentCategory: ItsmPractice;
	projectKey: string;
	onRefreshGroup: () => void;
	selectedGroupId: string;
	onGroupChange: (groupId: string, selectedQueueIds: number[]) => void;
	canManageQueues: boolean;
	renderSpotlight: boolean;
}) => (
	<>
		{renderSpotlight ? (
			<SpotlightTarget
				name={
					currentCategory === CHANGES
						? PRACTICES_IDS.CHANGES_QUEUE_ITEMS_SPOTLIGHT
						: PRACTICES_IDS.INCIDENTS_QUEUE_ITEMS_SPOTLIGHT
				}
			>
				<>
					<StarredSection practice={currentCategory} />
					<QueueGroupsSection
						projectKey={projectKey}
						currentCategory={currentCategory}
						onGroupDataRefresh={onRefreshGroup}
						currentCategorySelectedGroupId={selectedGroupId}
						onGroupChange={onGroupChange}
						canManageQueues={canManageQueues}
					/>
					<AllQueuesSection practice={currentCategory} />
				</>
			</SpotlightTarget>
		) : (
			<>
				<StarredSection practice={currentCategory} />
				<QueueGroupsSection
					projectKey={projectKey}
					currentCategory={currentCategory}
					onGroupDataRefresh={onRefreshGroup}
					currentCategorySelectedGroupId={selectedGroupId}
					onGroupChange={onGroupChange}
					canManageQueues={canManageQueues}
				/>
				<AllQueuesSection practice={currentCategory} />
			</>
		)}
	</>
);

const PracticeQueuesError = ({ title, onRefresh }: { title: ReactNode; onRefresh: () => void }) => {
	const { formatMessage } = useIntl();
	return (
		<>
			<TitleHeader>{title}</TitleHeader>
			<Box padding="space.100">
				{formatMessage(messages.fetchQueuesError, {
					button: (buttonLabel: ReactNode) => (
						<Button spacing="none" appearance="link" onClick={onRefresh}>
							{buttonLabel}
						</Button>
					),
				})}
			</Box>
		</>
	);
};

/**
 * @deprecated This is a Nav3 component. If you do any changes here, please apply and QA them at Nav4 component as well: src/packages/navigation-apps/sidebar-nav4
 */
const PracticeQueuesInternal = ({ practice = undefined, title }: PracticeQueuesProps) => {
	const { formatMessage } = useIntl();
	const { data: project, loading: projectLoading, error: projectError } = useProject();
	const { data: connectItems } = useProjectQueuesConnectItems();
	const currentCategory = practice || NO_CATEGORY;
	const [queues, queuesLoading, queuesError, methods, refreshQueues] = useCategorizedNavItems(
		'',
		project ? project.key : '',
		currentCategory,
	);
	const [metadata] = usePracticeMetadata(practice);
	const features = useQueueFeatures();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { shouldUseJSM } = useOgTenantInfo();
	const queueCustomContext = useQueueCustomRouterContext(project?.key);
	const [groups, groupLoading, groupFetchingError, refresh] = usePriorityGroups(
		currentCategory,
		queueCustomContext,
	);
	const [currentCategorySelectedGroupId, navigationCategoryActions] =
		useSelectedGroupIdByCategory(currentCategory);

	useOnFavoritesChanged(
		project?.key || '',
		practice || NO_CATEGORY,
		queues,
		methods.editItemFavorite,
		methods.refreshIssueCountForItems,
	);
	const isCountDataRequired = Boolean(metadata && !metadata.hasRequestedCount);

	const selectFirstGroupAsDefault = useCallback(() => {
		navigationCategoryActions.updateCurrentCategorySelectedGroup(
			// eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain, @typescript-eslint/no-non-null-assertion
			project?.key!,
			currentCategory,
			groups[0].id,
		);

		methods.enableIssueCountForSelectedQueues(groups[0].queues);
	}, [currentCategory, groups, methods, navigationCategoryActions, project?.key]);

	useEffect(() => {
		const shouldUpdateIssueCounts =
			groupLoading ||
			queuesLoading ||
			projectLoading ||
			groupFetchingError ||
			queuesError ||
			projectError;

		if (shouldUpdateIssueCounts) {
			return undefined;
		}
		if (groups.length === 0) {
			return methods.enableIssueCountForSelectedQueues([]);
		}
		if (currentCategorySelectedGroupId === undefined) {
			return selectFirstGroupAsDefault();
		}
		const selectedGroup = groups.find((group) => group.id === currentCategorySelectedGroupId);
		if (selectedGroup) {
			return methods.enableIssueCountForSelectedQueues(selectedGroup.queues);
		}
		return selectFirstGroupAsDefault();
	}, [
		currentCategorySelectedGroupId,
		groups,
		groupFetchingError,
		groupLoading,
		methods,
		selectFirstGroupAsDefault,
		queuesError,
		projectError,
		queuesLoading,
		projectLoading,
	]);

	useEffect(() => {
		if (isCountDataRequired) {
			const event = createAnalyticsEvent({ action: 'mounted' });
			methods.initialFetchNavItems(event);
		}
	}, [createAnalyticsEvent, isCountDataRequired, methods]);

	// fix connect links to new urls
	const queuesConnectItems = useMemo(() => connectItems?.map(updateConnectUrl), [connectItems]);
	const renderSpotlight =
		// @ts-expect-error - TS2345 - Argument of type 'ItsmPractice | undefined' is not assignable to parameter of type '"change-management" | "incident-management"'.
		features.canMoveQueues && [CHANGES, INCIDENTS].includes(practice) && !__SERVER__;

	const showOpsgenieIncidentsMenuItem = practice === INCIDENTS && !shouldUseJSM;

	const onGroupChange = useCallback(
		(groupId: string, selectedQueueIds: number[]) => {
			if (project?.key) {
				navigationCategoryActions.updateCurrentCategorySelectedGroup(
					project?.key,
					currentCategory,
					groupId,
				);
			}

			methods.enableIssueCountForSelectedQueues(selectedQueueIds);
			methods.refreshIssueCountForItems(selectedQueueIds.map((queueId) => queueId.toString()));
		},
		[currentCategory, methods, navigationCategoryActions, project?.key],
	);

	if (projectError || queuesError) {
		if (!fg('update_viewqueuesnav_instrumentation')) {
			return <PracticeQueuesError title={title} onRefresh={refreshQueues} />;
		}

		return (
			<ExperienceFailureTracker
				location="failed-fetching-initial-data"
				failureEventAttributes={{
					errorName: queuesError?.name || projectError?.name,
					errorMessage: queuesError?.message || projectError?.message,
					errorStack: queuesError?.stack || projectError?.stack,
				}}
			>
				<PracticeQueuesError title={title} onRefresh={refreshQueues} />
			</ExperienceFailureTracker>
		);
	}

	return (
		// DESK-5408 remove this QueuesExperienceTracker
		<QueuesExperienceTracker experience="viewQueuesNav">
			{/* @ts-expect-error - TS2345 - Argument of type 'ItsmPractice | undefined' is not assignable to parameter of type '"change-management" | "incident-management"'. */}
			{[CHANGES, INCIDENTS].includes(practice) ? (
				/* NOTE: These SpotlightTargets can be used throughout Jira not just within
				 * the current package. They're often used for onboarding or changeboarding
				 * experiences and allow an Atlaskit spotlight to be attached to page elements.
				 * If you need to change this, make sure you check where the target ID is being used
				 * and contact the relevant team as necessary. This Sourcegraph link should help :)
				 * http://go/src/_QUEUE_.*_SPOTLIGHT|-queue-.*-spotlight
				 */
				<SpotlightTarget
					name={
						practice === CHANGES
							? PRACTICES_IDS.CHANGES_QUEUE_TITLE_SPOTLIGHT
							: PRACTICES_IDS.INCIDENTS_QUEUE_TITLE_SPOTLIGHT
					}
				>
					<TitleHeader>{title}</TitleHeader>
				</SpotlightTarget>
			) : (
				<TitleHeader>{title}</TitleHeader>
			)}
			<SectionsWithQueueGroups
				currentCategory={currentCategory}
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
				projectKey={project?.key!}
				onRefreshGroup={refresh}
				selectedGroupId={currentCategorySelectedGroupId || ''}
				onGroupChange={onGroupChange}
				canManageQueues={features.canManageQueues}
				renderSpotlight={renderSpotlight}
			/>
			{showOpsgenieIncidentsMenuItem && <GotoOpsgenieMenuItem />}
			{(!practice || practice === NO_CATEGORY) && (
				<ConnectMenu
					items={queuesConnectItems}
					showIcons={false}
					overrides={{
						BackMenuItem: {
							href: `/jira/servicedesk/projects/${project?.key}/${
								QUEUE_ROUTES[practice || NO_CATEGORY]
							}/`,
							onClick: (ev) => ev.preventDefault(),
							title,
						},
					}}
				/>
			)}
			{features.canManageQueues === true && (
				<Section hasSeparator>
					<ManageQueuesItem practice={practice || NO_CATEGORY}>
						{formatMessage(messages.itemQueueSettings)}
					</ManageQueuesItem>
					<GiveFeedbackItem />
				</Section>
			)}
			{features.canManageQueues !== true && (
				<Section hasSeparator>
					<GiveFeedbackItem />
				</Section>
			)}
			{(!practice || practice === NO_CATEGORY) && <ForgeServiceDeskQueueSection />}
		</QueuesExperienceTracker>
	);
};

const PracticeQueuesWithExperienceTracker = ({
	practice = undefined,
	title,
}: PracticeQueuesProps) => {
	return (
		<QueuesExperienceTracker experience="viewQueuesNavExtended">
			<PracticeQueuesInternal practice={practice} title={title} />
		</QueuesExperienceTracker>
	);
};

export const PracticeQueues = componentWithFG(
	'update_viewqueuesnav_instrumentation',
	PracticeQueuesWithExperienceTracker,
	PracticeQueuesInternal,
);
